import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import ProvenanceGraph from './components/ProvenanceGraph';
import TransactionDetails from './components/TransactionDetails';
import ProvenanceFilters from './components/ProvenanceFilters';
import ProvenanceTimeline from './components/ProvenanceTimeline';
import ComplianceAlerts from './components/ComplianceAlerts';

const ProvenanceTracking = () => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'timeline'
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setViewMode('timeline');
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mock credit data for search functionality
  const mockCredits = [
    {
      id: 'VCS-2024-001-12345',
      serialNumber: 'VCS-2024-001-12345',
      registry: 'Verra VCS',
      projectName: 'Amazon Rainforest Conservation Project',
      amount: 1000,
      status: 'Active',
      issuanceDate: '2024-01-15',
      methodology: 'VM0007'
    },
    {
      id: 'GS-2024-001-54321',
      serialNumber: 'GS-2024-001-54321',
      registry: 'Gold Standard',
      projectName: 'Solar Energy Development Kenya',
      amount: 750,
      status: 'Retired',
      issuanceDate: '2024-02-10',
      methodology: 'GS-TPDDTEC'
    }
  ];

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // In a real app, this would trigger data refetch
    console.log('Filters updated:', newFilters);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    // In a real app, this would trigger search functionality
    console.log('Search query:', query);
  };

  const handleEventSelect = (event) => {
    setSelectedNode(event);
  };

  const handleCreditSelect = (credit) => {
    setSelectedCredit(credit);
  };

  const clearSelection = () => {
    setSelectedNode(null);
  };

  const exportProvenanceReport = () => {
    // Mock export functionality
    console.log('Exporting provenance report...');
    alert('Provenance report export initiated. You will receive an email when ready.');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="GitBranch" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Provenance Tracking</h1>
                <p className="text-sm text-muted-foreground">
                  Comprehensive credit lineage visualization from issuance to retirement
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* View Mode Toggle */}
              {!isMobile && (
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('graph')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                      viewMode === 'graph' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Network" size={16} className="mr-2" />
                    Graph View
                  </button>
                  <button
                    onClick={() => setViewMode('timeline')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
                      viewMode === 'timeline' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Clock" size={16} className="mr-2" />
                    Timeline View
                  </button>
                </div>
              )}

              <button
                onClick={exportProvenanceReport}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
              >
                <Icon name="Download" size={16} />
                <span className="hidden sm:inline">Export Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Filters Section */}
          <div className="lg:col-span-12">
            <ProvenanceFilters
              onFiltersChange={handleFiltersChange}
              onSearch={handleSearch}
            />
          </div>

          {/* Main Visualization Area */}
          <div className="lg:col-span-8">
            <div className="space-y-6">
              {/* Graph or Timeline View */}
              {viewMode === 'graph' ? (
                <ProvenanceGraph
                  selectedCredit={selectedCredit}
                  onNodeSelect={handleNodeSelect}
                  filters={filters}
                  className="h-96"
                />
              ) : (
                <ProvenanceTimeline
                  selectedCredit={selectedCredit}
                  onEventSelect={handleEventSelect}
                />
              )}

              {/* Compliance Alerts */}
              <ComplianceAlerts />
            </div>
          </div>

          {/* Details Panel */}
          <div className="lg:col-span-4">
            <div className="sticky top-6 space-y-6">
              <TransactionDetails
                selectedNode={selectedNode}
                onClose={clearSelection}
              />

              {/* Quick Search */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Credit Lookup</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Enter serial number..."
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-smooth">
                    <Icon name="Search" size={16} />
                    <span>Search Credit</span>
                  </button>
                </div>

                {/* Recent Searches */}
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium text-foreground mb-2">Recent Searches</h4>
                  <div className="space-y-1">
                    {mockCredits?.slice(0, 3)?.map((credit) => (
                      <button
                        key={credit?.id}
                        onClick={() => handleCreditSelect(credit)}
                        className="w-full text-left p-2 hover:bg-muted rounded-lg transition-smooth"
                      >
                        <div className="text-xs font-mono text-foreground">{credit?.serialNumber}</div>
                        <div className="text-xs text-muted-foreground truncate">{credit?.projectName}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* System Status */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">System Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Registry Connections</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-sm text-success">6/6 Online</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Data Sync Status</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                      <span className="text-sm text-success">Real-time</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Last Update</span>
                    <span className="text-sm text-foreground">2 minutes ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4">
          <div className="flex items-center justify-around">
            <button
              onClick={() => setViewMode('timeline')}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-smooth ${
                viewMode === 'timeline' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
            >
              <Icon name="Clock" size={20} />
              <span className="text-xs">Timeline</span>
            </button>
            <button
              onClick={exportProvenanceReport}
              className="flex flex-col items-center space-y-1 px-3 py-2 text-muted-foreground rounded-lg transition-smooth"
            >
              <Icon name="Download" size={20} />
              <span className="text-xs">Export</span>
            </button>
            <button className="flex flex-col items-center space-y-1 px-3 py-2 text-muted-foreground rounded-lg transition-smooth">
              <Icon name="Search" size={20} />
              <span className="text-xs">Search</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvenanceTracking;